import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Search, Filter } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCurrency } from "@/contexts/CurrencyContext";
import CurrencySelector from "@/components/CurrencySelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== "all") {
        params.category = selectedCategory;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await axios.get(`${API}/products`, { params });
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 95% OFF Banner */}
      <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white py-2 px-4">
        <p className="text-center text-sm sm:text-base font-bold animate-pulse">
          🔥 MASSIVE SALE: 95% OFF ALL FORMULAS! Limited Time Only! 🔥
        </p>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
            <div className="flex items-center gap-6">
              <nav className="hidden md:flex gap-8 items-center">
                <Link to="/products" className="text-slate-900 font-medium">
                  Products
                </Link>
                <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                  Home
                </Link>
                <CurrencySelector />
              </nav>
            </div>
          </div>
        </div>
      </header>

      {/* Page Header */}
      <div className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="products-page-title">
            Business Formulas & Packages
          </h1>
          <p className="text-lg text-slate-600">
            Browse our complete collection of manufacturing formulas and business setup guides
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search formulas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-lg border-slate-200 focus:border-slate-900"
              data-testid="search-input"
            />
          </div>
          <div className="sm:w-64">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="h-12 rounded-lg border-slate-200" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12" data-testid="no-products">
            <p className="text-slate-600 text-lg">No products found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
            {products.map((product) => (
              <Link 
                key={product.id} 
                to={`/products/${product.id}`}
                className="group relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:border-slate-300"
                data-testid={`product-card-${product.id}`}
              >
                {/* Discount Badge */}
                {product.discount_percentage > 0 && (
                  <div className="absolute top-4 left-4 z-10 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {product.discount_percentage}% OFF
                  </div>
                )}
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={product.image_url} 
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="text-sm text-sky-600 font-medium mb-2">{product.category}</div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-sky-600 transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {product.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  {product.features && product.features.length > 0 && (
                    <ul className="space-y-1 mb-4">
                      {product.features.slice(0, 2).map((feature, idx) => (
                        <li key={idx} className="text-xs text-slate-500 flex items-center gap-2">
                          <div className="bg-sky-500 rounded-full w-1 h-1" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      {product.original_price && product.original_price !== product.price && (
                        <span className="text-sm text-slate-400 line-through" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {formatPrice(product.original_price)}
                        </span>
                      )}
                      <span className="text-2xl font-bold text-red-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {formatPrice(product.price)}
                      </span>
                    </div>
                    <Button 
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                      size="sm"
                      data-testid={`view-details-btn-${product.id}`}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400">
            © 2026 Formula Library. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/refund-policy" className="text-slate-400 hover:text-white transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Products;
